import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BookOpen, Search, ExternalLink } from "lucide-react";

/**
 * Docs — in-app rendering of `docs/USER_GUIDE.md` (also exposed at
 * `/docs/USER_GUIDE.md` for download).
 *
 * Pure read-only viewer. Markdown is fetched once on mount, parsed
 * with react-markdown + remark-gfm (tables / strikethrough / task
 * lists), and rendered into a 2-column layout: collapsible
 * table-of-contents sidebar (extracted from the H2 headings) + the
 * scrollable body. Anchor links inside the body are remapped to
 * smooth-scroll within the page so the sidebar TOC works without a
 * router round-trip.
 */
export default function Docs() {
    const [md, setMd] = useState("");
    const [err, setErr] = useState("");
    const [query, setQuery] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await fetch("/docs/USER_GUIDE.md", { credentials: "omit" });
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const text = await r.text();
                if (!cancelled) setMd(text);
            } catch (e) {
                if (!cancelled) setErr(`Couldn't load user guide: ${e.message}`);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Scroll-to-hash after markdown finishes loading. The browser's
    // native hash-jump fires before the document has been rendered, so
    // we re-run it once `md` is populated and React has painted the
    // headings (next frame). Also re-runs on hash change so the floating
    // help button can navigate between sections without a reload.
    useEffect(() => {
        if (!md) return;
        const hash = window.location.hash?.slice(1);
        if (!hash) return;
        const id = requestAnimationFrame(() => {
            const el = document.getElementById(hash);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        });
        return () => cancelAnimationFrame(id);
    }, [md]);

    // Build TOC from H2 / H3 headings — same anchor algo as
    // react-markdown's slugger.
    const toc = useMemo(() => {
        if (!md) return [];
        const rows = [];
        for (const line of md.split("\n")) {
            const h2 = line.match(/^##\s+(.+)$/);
            const h3 = line.match(/^###\s+(.+)$/);
            if (h2) rows.push({ level: 2, text: h2[1].trim() });
            else if (h3) rows.push({ level: 3, text: h3[1].trim() });
        }
        return rows;
    }, [md]);

    const slug = (t) =>
        t.toLowerCase()
            .replace(/[^\w\s-]/g, "")
            .trim()
            .replace(/\s+/g, "-");

    const filtered = useMemo(() => {
        if (!query.trim()) return toc;
        const q = query.toLowerCase();
        return toc.filter((r) => r.text.toLowerCase().includes(q));
    }, [toc, query]);

    return (
        <div className="mx-auto max-w-7xl p-6" data-testid="docs-page">
            <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-[#5b5ef0]/40 bg-[#5b5ef0]/10 text-[#5b5ef0]">
                        <BookOpen size={20} />
                    </div>
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#5b5ef0]">
                            User guide
                        </div>
                        <h1 className="font-display text-2xl font-bold sm:text-[28px]">
                            How to use MoovBytes
                        </h1>
                        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                            The canonical operator&apos;s manual — getting started, every page,
                            common workflows, and troubleshooting.
                        </p>
                    </div>
                </div>
                <a
                    href="/docs/USER_GUIDE.md"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="docs-download"
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-2 text-xs font-semibold uppercase tracking-widest hover:border-foreground/40"
                >
                    Markdown <ExternalLink size={11} />
                </a>
            </div>

            {err && (
                <div className="rounded-lg border border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 p-4 text-sm" data-testid="docs-error">
                    {err}
                </div>
            )}

            {!err && (
                <div className="grid gap-5 lg:grid-cols-[240px_1fr]">
                    {/* TOC */}
                    <aside className="hidden lg:block">
                        <div className="sticky top-4 rounded-xl border border-border bg-surface p-3" data-testid="docs-toc">
                            <div className="relative mb-2">
                                <Search size={11} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <input
                                    type="text"
                                    placeholder="Search…"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    data-testid="docs-toc-search"
                                    className="w-full rounded-md border border-input bg-background py-1.5 pl-6 pr-2 text-xs focus:border-[#5b5ef0] focus:outline-none focus:ring-1 focus:ring-[#5b5ef0]/30"
                                />
                            </div>
                            <ul className="max-h-[70vh] overflow-y-auto pr-1">
                                {filtered.map((row, i) => (
                                    <li key={i}>
                                        <a
                                            href={`#${slug(row.text)}`}
                                            className={[
                                                "block rounded-md px-2 py-1 text-[12px] transition-colors hover:bg-muted/40 hover:text-foreground",
                                                row.level === 3 ? "pl-5 text-muted-foreground" : "font-semibold",
                                            ].join(" ")}
                                        >
                                            {row.text}
                                        </a>
                                    </li>
                                ))}
                                {filtered.length === 0 && (
                                    <li className="px-2 py-3 text-[11px] text-muted-foreground">No headings match.</li>
                                )}
                            </ul>
                        </div>
                    </aside>

                    {/* Body */}
                    <article className="prose prose-sm mb-prose max-w-none rounded-xl border border-border bg-surface p-6 sm:p-8" data-testid="docs-body">
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                                h1: ({ ...props }) => <h1 className="mb-3 font-display text-3xl font-bold" {...props} />,
                                h2: ({ children, ...rest }) => (
                                    <h2
                                        id={typeof children === "string" ? slug(children) : slug(String(children))}
                                        className="mt-8 border-b border-border pb-2 font-display text-xl font-bold"
                                        {...rest}
                                    >
                                        {children}
                                    </h2>
                                ),
                                h3: ({ children, ...rest }) => (
                                    <h3
                                        id={typeof children === "string" ? slug(children) : slug(String(children))}
                                        className="mt-6 font-display text-base font-semibold"
                                        {...rest}
                                    >
                                        {children}
                                    </h3>
                                ),
                                a: ({ ...props }) => <a className="text-[#5b5ef0] hover:underline" {...props} />,
                                code: ({ inline, ...props }) =>
                                    inline ? (
                                        <code className="rounded bg-muted px-1 py-0.5 font-mono text-[12px]" {...props} />
                                    ) : (
                                        <code className="block rounded-md bg-muted p-3 font-mono text-[12px]" {...props} />
                                    ),
                                table: ({ ...props }) => (
                                    <div className="my-3 overflow-x-auto rounded-md border border-border">
                                        <table className="w-full text-sm" {...props} />
                                    </div>
                                ),
                                th: ({ ...props }) => <th className="border-b border-border bg-background/40 px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-widest text-muted-foreground" {...props} />,
                                td: ({ ...props }) => <td className="border-b border-border px-3 py-2 text-sm" {...props} />,
                                blockquote: ({ ...props }) => (
                                    <blockquote className="my-3 rounded-md border-l-4 border-[#5b5ef0]/50 bg-[#5b5ef0]/[0.04] p-3 text-sm italic" {...props} />
                                ),
                            }}
                        >
                            {md}
                        </ReactMarkdown>
                    </article>
                </div>
            )}
        </div>
    );
}
